import { CURRENCIES, DEFAULT_CURRENCY, YEARLY_MONTHS_CHARGED } from '../content/pricing.js';

/**
 * Formats a published price.
 *
 * Two deliberate decisions:
 *
 * 1. The symbol is prepended manually rather than using
 *    Intl.NumberFormat({ style: 'currency' }). That API renders the ISO code
 *    ("USD 149") as often as the symbol depending on browser and OS, and it
 *    was doing the same for KES/UGX before. A manual prefix is the only
 *    reliable path.
 *
 * 2. Zero fraction digits. A published price of "$149.00" reads like an
 *    invoice, not a price.
 */
export function formatPrice(amount, currencyCode) {
  if (amount == null) return null;
  // Falls back through DEFAULT_CURRENCY to the first entry, so a currency code
  // left in localStorage by an older build can never resolve to undefined and
  // throw on .symbol.
  const currency =
    CURRENCIES[currencyCode] ?? CURRENCIES[DEFAULT_CURRENCY] ?? Object.values(CURRENCIES)[0];
  if (!currency) return null;

  const digits = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
  return `${currency.symbol}${digits}`;
}

/** Yearly total, charged as YEARLY_MONTHS_CHARGED months. */
export function yearlyTotal(monthly) {
  if (monthly == null) return null;
  return monthly * YEARLY_MONTHS_CHARGED;
}

/**
 * Effective monthly cost when paying yearly — this is the number shown on the
 * card, because comparing a yearly total against a monthly price misleads.
 */
export function yearlyPerMonth(monthly) {
  if (monthly == null) return null;
  return Math.round(yearlyTotal(monthly) / 12);
}

/**
 * Discount percentage, DERIVED rather than hardcoded, so the badge can never
 * drift out of sync with the prices above it.
 */
export function yearlySavingPercent() {
  return Math.round((1 - YEARLY_MONTHS_CHARGED / 12) * 100);
}

/**
 * Best-effort currency guess from the browser locale; always overridable.
 * Only ever returns a code that actually exists in CURRENCIES.
 */
export function guessCurrency(fallback = DEFAULT_CURRENCY) {
  const safe = CURRENCIES[fallback] ? fallback : Object.keys(CURRENCIES)[0];
  if (typeof navigator === 'undefined') return safe;

  const langs = navigator.languages ?? [navigator.language ?? ''];
  const guess = langs.some((l) => /-UG$/i.test(l)) ? 'UGX' : safe;
  return CURRENCIES[guess] ? guess : safe;
}
